import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type {
  AronKnowledgeNote,
  AronKnowledgeNoteInput,
  AronKnowledgeStatus,
} from "../types";

const BASE = "/aron/knowledge-notes/";

export const aronKnowledgeKeys = {
  all: ["aron", "knowledge-notes"] as const,
  list: (status?: AronKnowledgeStatus) =>
    ["aron", "knowledge-notes", status ?? "all"] as const,
};

export interface KnowledgeListResult {
  notes: AronKnowledgeNote[];
  count: number;
}

// The knowledge base is small (curated notes), so we pull the whole filtered set in
// one page (max_page_size = 500) instead of wiring pagination into the panel.
export function useKnowledgeNotes(status?: AronKnowledgeStatus) {
  return useQuery<KnowledgeListResult>({
    queryKey: aronKnowledgeKeys.list(status),
    queryFn: async () => {
      const params = new URLSearchParams({ page_size: "500" });
      if (status) params.set("status", status);
      const r = await apiClient.get<{
        data: AronKnowledgeNote[];
        meta: { count?: number };
      }>(`${BASE}?${params.toString()}`);
      return { notes: r.data.data, count: r.data.meta?.count ?? r.data.data.length };
    },
    staleTime: 30_000,
  });
}

// Count of PENDING ARON suggestions — drives the sidebar badge. Shares the
// ["aron","knowledge-notes"] key prefix, so accept/reject refreshes it too.
export function usePendingKnowledgeCount(enabled: boolean) {
  return useQuery<number>({
    queryKey: ["aron", "knowledge-notes", "pending-count"],
    enabled,
    queryFn: async () => {
      const r = await apiClient.get<{ meta: { count?: number } }>(
        `${BASE}?status=pending&page_size=1`,
      );
      return r.data.meta?.count ?? 0;
    },
    staleTime: 30_000,
  });
}

function useInvalidateKnowledge() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: aronKnowledgeKeys.all });
}

export function useCreateKnowledgeNote() {
  const invalidate = useInvalidateKnowledge();
  return useMutation({
    mutationFn: async (input: AronKnowledgeNoteInput) => {
      const r = await apiClient.post<{ data: AronKnowledgeNote }>(BASE, input);
      return r.data.data;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateKnowledgeNote() {
  const invalidate = useInvalidateKnowledge();
  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: { id: string } & Partial<AronKnowledgeNoteInput>) => {
      const r = await apiClient.patch<{ data: AronKnowledgeNote }>(`${BASE}${id}/`, input);
      return r.data.data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteKnowledgeNote() {
  const invalidate = useInvalidateKnowledge();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`${BASE}${id}/`);
    },
    onSuccess: invalidate,
  });
}

// Promote a PENDING ARON suggestion to ACTIVE — optionally editing it first.
export function useAcceptSuggestion() {
  const invalidate = useInvalidateKnowledge();
  return useMutation({
    mutationFn: async ({
      id,
      edit,
    }: {
      id: string;
      edit?: Partial<AronKnowledgeNoteInput>;
    }) => {
      const r = await apiClient.post<{ data: AronKnowledgeNote }>(
        `${BASE}${id}/accept/`,
        edit ?? {},
      );
      return r.data.data;
    },
    onSuccess: invalidate,
  });
}

export function useRejectSuggestion() {
  const invalidate = useInvalidateKnowledge();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await apiClient.post<{ data: AronKnowledgeNote }>(`${BASE}${id}/reject/`, {});
      return r.data.data;
    },
    onSuccess: invalidate,
  });
}
